// ============================================================================
// SHARED INVOICE HTML RENDERER — renderInvoiceHtml.js
// ============================================================================
// Pure JavaScript — NO browser-only APIs (no window, no DOM, no document).
// Used both client-side (InvoicesPage.jsx) and server-side (api/invoice-pdf.js).
//
// Layout reference: Studio BRIO Pvt. Ltd. tax invoice template
// Typography: ITC Avant Garde Gothic family (Century Gothic fallback)
//
// Margins (from page edge):
//   Header image: 0 all sides — full bleed
//   Document title: 2cm left, 2cm right
//   Details (Client→Date): 2cm left, 2cm right
//   Amounts table: 5.5cm left, 2cm right
//   Signature block: 5.5cm left
//   Bank details footer: 5.5cm left, 2cm right, 2cm bottom
//
// Export: buildInvoiceHtml(pdfData) → full HTML document string
//         resolveInvoiceCurrency(pdfData) → 'EUR' | 'INR'
// ============================================================================

import { resolveBrandingImageSrc } from '../utils/brandingImages.js';

function formatDateLong(dateStr) {
  if (!dateStr) return '\u2014';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  const [yyyy, mm, dd] = parts;
  const day = parseInt(dd, 10);
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const month = monthNames[parseInt(mm, 10) - 1] || mm;
  let suffix = 'th';
  if (day === 1 || day === 21 || day === 31) suffix = 'st';
  else if (day === 2 || day === 22) suffix = 'nd';
  else if (day === 3 || day === 23) suffix = 'rd';
  return `${day}${suffix} ${month} ${yyyy}`;
}

/**
 * The single currency rule for an invoice render.
 *
 * This used to be decided twice: here, by `currency === 'EUR' || officeKey ===
 * 'rotterdam'`, and again in api/invoice-pdf.js by `invoice.currency ?? (...)`.
 * `??` only falls back on null, so a Rotterdam invoice carrying a stray
 * `currency = 'INR'` came out with euro symbols in the amounts table and an
 * amount in words reading "Rupees ... only". Both callers go through this now,
 * so the symbol and any words can no longer disagree.
 */
export function resolveInvoiceCurrency(pdfData) {
  if (pdfData?.currency === 'EUR') return 'EUR';
  return (pdfData?.officeKey || '').toLowerCase() === 'rotterdam' ? 'EUR' : 'INR';
}

export function buildInvoiceHtml(pdfData) {
  // ── Currency: one rule, shared with the PDF endpoint ──
  const isEur = resolveInvoiceCurrency(pdfData) === 'EUR';

  // Euros carry cents; rupees do not. Rounding each row to whole euros
  // independently made the printed rows disagree with each other and with the
  // amount actually owed (Almere invoice 602: 1.084 + 228 printed against a
  // total of 1.311). Whole rupees stays the Indian convention.
  const fmtN = (n) => {
    const num = Number(n) || 0;
    return isEur
      ? num.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : num.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  const debug = !!pdfData.debugPdf;

  // ── Currency symbol: € for EUR, ₹ for INR ──
  const curSymbol = isEur ? '&#x20AC;' : '&#x20B9;';

  // ── Images ──
  const headerDataUrl = resolveBrandingImageSrc(pdfData.headerImage);
  const sigDataUrl = resolveBrandingImageSrc(pdfData.signatureImage);

  // ── Signature block ──
  const sig = pdfData.signatureBlock || {};
  const companyLine = sig.companyLine || '';
  const hasSigBlock = !!(companyLine || sig.signatoryName || sig.signatoryTitle || sigDataUrl);

  // ── Tax rows ──
  const taxTotal = Number(pdfData.grossAmount) - Number(pdfData.baseAmount);
  let taxRowsHtml = '';

  if (pdfData.taxRegime === 'IN_intra_state') {
    const half = taxTotal / 2;
    taxRowsHtml = `
      <tr>
        <td class="a-lbl">CGST @ 9%</td>
        <td class="a-op">+</td>
        <td class="a-cur">${curSymbol}</td>
        <td class="a-amt">${fmtN(half)}</td>
      </tr>
      <tr>
        <td class="a-lbl">SGST @ 9%</td>
        <td class="a-op">+</td>
        <td class="a-cur">${curSymbol}</td>
        <td class="a-amt">${fmtN(half)}</td>
      </tr>`;
  } else if (pdfData.taxRegime === 'IN_inter_state') {
    taxRowsHtml = `
      <tr>
        <td class="a-lbl">IGST @ ${pdfData.gstPercent}%</td>
        <td class="a-op">+</td>
        <td class="a-cur">${curSymbol}</td>
        <td class="a-amt">${fmtN(taxTotal)}</td>
      </tr>`;
  } else if (pdfData.taxRegime === 'NL') {
    taxRowsHtml = `
      <tr>
        <td class="a-lbl">VAT @ ${pdfData.gstPercent}%</td>
        <td class="a-op">+</td>
        <td class="a-cur">${curSymbol}</td>
        <td class="a-amt">${fmtN(taxTotal)}</td>
      </tr>`;
  } else if (taxTotal > 0) {
    taxRowsHtml = `
      <tr>
        <td class="a-lbl">Tax @ ${pdfData.gstPercent}%</td>
        <td class="a-op">+</td>
        <td class="a-cur">${curSymbol}</td>
        <td class="a-amt">${fmtN(taxTotal)}</td>
      </tr>`;
  }

  // ── TDS + total rows ──
  let tdsRowsHtml = '';
  if (pdfData.applyTds && pdfData.tdsAmount > 0) {
    tdsRowsHtml = `
      <tr>
        <td class="a-lbl">Total</td>
        <td class="a-op"></td>
        <td class="a-cur">${curSymbol}</td>
        <td class="a-amt">${fmtN(pdfData.grossAmount)}</td>
      </tr>
      <tr>
        <td class="a-lbl">Less: TDS (${pdfData.tdsPercent}%)</td>
        <td class="a-op">\u2212</td>
        <td class="a-cur">${curSymbol}</td>
        <td class="a-amt">${fmtN(pdfData.tdsAmount)}</td>
      </tr>
      <tr class="a-total-row">
        <td class="a-lbl">Net Payable</td>
        <td class="a-op"></td>
        <td class="a-cur">${curSymbol}</td>
        <td class="a-amt">${fmtN(pdfData.netAmount)}</td>
      </tr>`;
  } else {
    tdsRowsHtml = `
      <tr class="a-total-row">
        <td class="a-lbl">Total</td>
        <td class="a-op"></td>
        <td class="a-cur">${curSymbol}</td>
        <td class="a-amt">${fmtN(pdfData.grossAmount)}</td>
      </tr>`;
  }

  // ── Total in words row ──
  // Indian invoices only. Writing the total out in words is an Indian invoicing
  // convention; a Dutch invoice does not carry one, so the row is omitted for
  // EUR rather than translated.
  //
  // Full-width cell (colspan 4) with a flex row so the value can use the whole
  // line width instead of being squeezed into the narrow amounts column — this
  // keeps long amounts to 1–2 lines rather than sprawling to 3.
  const wordsRowHtml = (!isEur && pdfData.totalInWords)
    ? `<tr class="a-words-row">
        <td class="a-words-cell" colspan="4">
          <div class="a-words-flex">
            <span class="a-words-lbl">Total Amount in Words:</span>
            <span class="a-words-val"><em>${pdfData.totalInWords}</em></span>
          </div>
        </td>
      </tr>`
    : '';

  // ── Bank details ──
  // `vatOrGst` holds one office's tax registration number, so its label follows
  // the office: the Dutch BTW identification number is not a GST number.
  const BANK_ORDER = [
    ['accountName',   'Bank Account Name'],
    ['accountNumber', 'Account Number'],
    ['bankName',      'Bank Name'],
    ['ifsc',          'IFSC Code'],
    ['pan',           'PAN:'],
    ['vatOrGst',      isEur ? 'VAT:' : 'GST:'],
    ['kvk',           'KvK:'],
    ['swift',         'SWIFT:'],
    ['iban',          'IBAN:'],
    ['branch',        'Branch:'],
    ['udyam',         'Udyam Reg. No.:'],
  ];
  const bankHtml = BANK_ORDER
    .filter(([k]) => pdfData.bankDetails?.[k])
    .map(([k, label]) => `<tr><td class="bk-lbl">${label}</td><td class="bk-val"><em>${pdfData.bankDetails[k]}</em></td></tr>`)
    .join('');

  // ── Document title ──
  // "Tax Invoice" is GST vocabulary. A Dutch invoice is just an invoice, so the
  // word is dropped for EUR. Proforma reads the same either way.
  const invoiceTitle = pdfData.invoiceDocType === 'proforma'
    ? 'Proforma Invoice'
    : (isEur ? 'Invoice' : 'Tax Invoice');

  // ── Header ──
  const headerHtml = headerDataUrl
    ? `<div class="header-img"><img src="${headerDataUrl}" crossorigin="anonymous" /></div>`
    : `<div class="header-text">BRIO \u2014 ${pdfData.officeLabel}</div>`;

  // ── Client/project detail rows ──
  const clientDisplayName = pdfData.clientName + (pdfData.clientOrganisation ? ' \u2014 ' + pdfData.clientOrganisation : '');
  const isB2B = (pdfData.invoiceType || '').toUpperCase() === 'B2B';

  // B2B: GST → PAN → warning (IN only)
  let gstPanRow = null;
  let b2bWarning = '';
  if (!isEur && isB2B) {
    if (pdfData.clientGst) {
      gstPanRow = { lbl: 'GST no.', val: pdfData.clientGst, bold: false };
    } else if (pdfData.clientPan) {
      gstPanRow = { lbl: 'PAN', val: pdfData.clientPan, bold: false };
    } else {
      b2bWarning = '<div class="b2b-warning">Warning: Client invoice data is incomplete (missing GST/PAN for B2B).</div>';
    }
  }

  // NL: VAT / BTW row
  const vatRow = (isEur && pdfData.clientVatNumber)
    ? { lbl: 'VAT / BTW no.', val: pdfData.clientVatNumber, bold: false }
    : null;

  const detailRows = [
    { lbl: 'Client Name',     val: clientDisplayName, bold: true },
    pdfData.clientAddress ? { lbl: 'Address', val: pdfData.clientAddress, bold: false } : null,
    { lbl: 'Project',         val: pdfData.projectName, bold: true },
    { lbl: 'Stage',           val: pdfData.stageLine, bold: false },
    { lbl: 'Invoice Number',  val: pdfData.invoiceNumber || '(Draft)', bold: false },
    !isEur && pdfData.invoiceType ? { lbl: 'Invoice Type', val: pdfData.invoiceType, bold: false } : null,
    !isEur ? gstPanRow : null,
    !isEur && pdfData.clientSacCode ? { lbl: 'SAC Code', val: pdfData.clientSacCode, bold: false } : null,
    !isEur && pdfData.clientPlaceOfSupply ? { lbl: 'Place of Supply', val: pdfData.clientPlaceOfSupply, bold: false } : null,
    vatRow,
    { lbl: 'Date:',           val: formatDateLong(pdfData.invoiceDate), bold: false },
    pdfData.dueDate ? { lbl: 'Payment Due:', val: `${formatDateLong(pdfData.dueDate)}${pdfData.paymentTermsDays != null ? ` (${pdfData.paymentTermsDays} days)` : ''}`, bold: false } : null,
  ].filter(Boolean).map(row => {
    const lblCls = row.bold ? 'dt-lbl dt-bold' : 'dt-lbl';
    const valCls = row.bold ? 'dt-val dt-bold' : 'dt-val';
    return `<tr><td class="${lblCls}">${row.lbl}</td><td class="${valCls}">${row.val}</td></tr>`;
  }).join('');

  // ── Signature ──
  const sigImgHtml = sigDataUrl
    ? `<img src="${sigDataUrl}" crossorigin="anonymous" class="sig-img" />`
    : '';
  const sigHtml = hasSigBlock ? `
    <div class="sig-section">
      ${companyLine ? `<div class="sig-company"><strong>For ${companyLine}</strong></div>` : ''}
      ${sigImgHtml}
      ${sig.signatoryName ? `<div class="sig-name">${sig.signatoryName}</div>` : ''}
      ${sig.signatoryTitle ? `<div class="sig-title">${sig.signatoryTitle}</div>` : ''}
    </div>` : (debug ? '<div class="sig-section" style="color:#999;font-size:7px;">Signature: NOT CONFIGURED</div>' : '');

  return `<!DOCTYPE html><html><head><meta charset="utf-8">
<link href="https://fonts.googleapis.com/css2?family=Noto+Sans:wght@300;400;500&display=swap" rel="stylesheet">
<style>
/* ─── Reset ─── */
* { box-sizing: border-box; margin: 0; padding: 0; }

/* ─── Page ─── */
@page { size: A4; margin: 0; }
html, body {
  width: 210mm;
  min-height: 297mm;
  margin: 0;
  padding: 0;
}
body {
  font-family: 'ITC Avant Garde Gothic Std', 'Century Gothic', CenturyGothic, AppleGothic, Arial, sans-serif;
  color: #222;
  font-size: 8pt;
  font-weight: 300;
  line-height: 1.55;
  display: flex;
  flex-direction: column;
}
img { max-width: 100%; }
table { page-break-inside: avoid; border-collapse: collapse; }
strong { font-weight: 500; }

/* ─── Header image: full bleed ─── */
.header-img { margin: 0; padding: 0; flex-shrink: 0; }
.header-img img { width: 100%; display: block; }
.header-text {
  font-size: 18px; font-weight: 300; color: #9F902A;
  padding: 24px 2cm 12px;
  border-bottom: 1px solid #ddd;
  flex-shrink: 0;
}

/* ─── Main content ─── */
.main-content {
  flex: 1;
  padding: 20px 2cm 0 2cm;
}

/* ─── TAX INVOICE title: moved up 0.8cm, nudged 0.3cm right ─── */
.inv-title {
  font-size: 18pt;
  font-weight: 300;
  text-transform: uppercase;
  letter-spacing: 4px;
  color: #808080;
  text-align: right;
  margin-top: -0.8cm;
  margin-right: -0.1cm;
  margin-bottom: 24px;
}

/* ─── Details table: line-height tuned ─── */
table.details { width: 100%; margin-bottom: 24px; }
table.details td {
  padding: 2px 0;
  vertical-align: top;
  font-size: 8pt;
  font-weight: 300;
  line-height: 1.265;
}
td.dt-lbl { color: #222; width: 130px; padding-right: 16px; }
td.dt-val { color: #222; }
td.dt-bold {
  font-family: 'ITC Avant Garde Gothic Md', 'ITC Avant Garde Gothic Std', 'Century Gothic', CenturyGothic, AppleGothic, Arial, sans-serif;
  font-weight: 500;
}

/* ─── Amounts wrapper: 5.5cm from page edge ─── */
/* main-content has 2cm left, so extra offset = 3.5cm */
.amounts-wrapper {
  margin-left: 3.5cm;
  margin-bottom: 6px;
}

/* ─── Amounts table: thin solid borders ─── */
table.amounts {
  width: 100%;
  border-spacing: 0;
  border: 0.5px solid #BDB470;
  padding: 0;
}
table.amounts td {
  padding: 7px 10px;
  font-size: 8pt;
  font-weight: 300;
  border: none;
  border-bottom: 0.5px solid #BDB470;
}
table.amounts tr:last-child td { border-bottom: none; }
td.a-lbl { text-align: left; color: #222; width: 65%; }
td.a-op  { text-align: center; width: 24px; color: #666; }
td.a-cur { text-align: left; width: 30px; color: #222; padding-left: 6px; padding-right: 0; font-family: 'Noto Sans', Arial, sans-serif; }
td.a-amt { text-align: right; width: 70px; color: #222; }
tr.a-total-row td {
  font-weight: 500;
  border: none;
  border-top: 0.5px solid #BDB470;
}

/* ─── Total in words: full-width cell, label left + value right ─── */
tr.a-words-row td {
  font-size: 8pt;
  font-weight: 300;
  padding: 8px 10px;
  border: none;
  border-top: 0.5px solid #BDB470;
}
.a-words-flex {
  display: flex;
  align-items: baseline;
  gap: 12px;
}
.a-words-lbl {
  white-space: nowrap;
  font-weight: 500;
  font-style: normal;
  flex: 0 0 auto;
}
.a-words-val {
  flex: 1 1 auto;
  min-width: 0;
  font-style: italic;
  font-weight: 300;
  text-align: right;
}

/* ─── Note ─── */
.note { margin: 10px 0; color: #666; font-size: 8pt; font-style: italic; margin-left: 3.5cm; }

/* ─── B2B warning ─── */
.b2b-warning {
  margin: 4px 0 12px;
  padding: 4px 8px;
  font-size: 7pt;
  color: #8B6914;
  background: #FFF8E1;
  border: 0.5pt solid #E6D590;
}

/* ─── Signature: 5.5cm from page edge ─── */
.sig-section { margin-top: 24px; margin-left: 3.5cm; }
.sig-company { font-size: 8pt; font-weight: 500; color: #222; margin-bottom: 6px; }
.sig-img     { display: block; height: 55px; margin: 10px 0; }
.sig-name    { font-size: 8pt; font-weight: 300; color: #222; }
.sig-title   { font-size: 8pt; font-weight: 300; color: #222; }

/* ─── Bank details footer: 5.5cm left, 2cm right, 2cm bottom ─── */
.bank-footer {
  flex-shrink: 0;
  margin-top: auto;
  padding: 0 2cm 2cm 5.5cm;
}
table.bank { width: 100%; }
td.bk-lbl {
  color: #222;
  font-size: 7pt;
  font-weight: 300;
  padding: 1.5px 12px 1.5px 0;
  width: 130px;
  vertical-align: top;
}
td.bk-val {
  font-size: 7pt;
  font-weight: 300;
  font-style: italic;
  padding: 1.5px 0;
  color: #222;
}
</style></head><body>

${headerHtml}

<div class="main-content">
  <div class="inv-title">${invoiceTitle}</div>

  <table class="details">${detailRows}</table>

  ${b2bWarning}

  <div class="amounts-wrapper">
    <table class="amounts">
      <tr>
        <td class="a-lbl">Professional Fees</td>
        <td class="a-op"></td>
        <td class="a-cur">${curSymbol}</td>
        <td class="a-amt">${fmtN(pdfData.baseAmount)}</td>
      </tr>
      ${taxRowsHtml}
      ${tdsRowsHtml}
      ${wordsRowHtml}
    </table>
  </div>

  ${pdfData.note ? `<div class="note">Note: ${pdfData.note}</div>` : ''}

  ${sigHtml}
</div>

${bankHtml ? `
<div class="bank-footer">
  <table class="bank">${bankHtml}</table>
</div>` : (debug ? '<div class="bank-footer" style="color:#999;font-size:7px;padding:0 2cm 2cm 5.5cm;">Bank details: MISSING</div>' : '')}

${debug ? `<div style="position:fixed;bottom:4px;right:4px;padding:6px;background:#f9f9f3;border:1px solid #eee;font-size:6px;color:#888;line-height:1.3;max-width:220px;">
  <strong>Debug</strong><br/>
  office: ${pdfData.officeKey} | inv#: ${pdfData.invoiceNumber || '(draft)'}<br/>
  header: ${!!headerDataUrl} | sig: ${!!sigDataUrl}<br/>
  bank: ${Object.keys(pdfData.bankDetails || {}).filter(k => pdfData.bankDetails[k]).length} fields<br/>
  address: ${pdfData.clientAddress ? 'YES' : 'MISSING'}<br/>
  invoiceType: ${pdfData.invoiceType || 'MISSING'} | B2B: ${isB2B}<br/>
  clientGst: ${pdfData.clientGst || 'MISSING'}<br/>
  clientPan: ${pdfData.clientPan || 'MISSING'}<br/>
  sacCode: ${pdfData.clientSacCode || 'MISSING'}<br/>
  placeOfSupply: ${pdfData.clientPlaceOfSupply || 'MISSING'}<br/>
  clientVatNumber: ${pdfData.clientVatNumber || 'MISSING'}<br/>
  currency: ${isEur ? 'EUR' : 'INR'} | totalInWords: ${isEur ? 'N/A (EUR)' : (pdfData.totalInWords ? 'YES' : 'MISSING')}
</div>` : ''}

</body></html>`;
}

export default buildInvoiceHtml;
